import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import api, { BRAND } from "@/lib/appApi";
import TrialBanner from "@/components/TrialBanner";
import { toast } from "sonner";
import { Check, Crown, ArrowLeft, Loader2 } from "lucide-react";

const PLANS = [
  { key: "premium_monthly", name: "Monthly", price: "$19", per: "/month", highlight: false },
  { key: "premium_6month", name: "6 Months", price: "$99", per: "/6 months", note: "Save ~13%", highlight: true },
  { key: "premium_yearly", name: "Yearly", price: "$149", per: "/year", note: "Best value · Save ~35%", highlight: false },
];

const PERKS = [
  "Choose your own gender",
  "Filter matches by gender (multi-select)",
  "Priority matching",
  "Everything in the free tier",
];

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loadingKey, setLoadingKey] = useState(null);

  const checkout = async (key) => {
    if (!user) {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = window.location.origin + "/pricing";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      return;
    }
    setLoadingKey(key);
    try {
      const res = await api.post("/payments/checkout", { lookup_key: key, origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout. Try again.");
      setLoadingKey(null);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-hidden">
      <TrialBanner />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full opacity-15 blur-[120px] pointer-events-none"
           style={{ background: "radial-gradient(circle, #a855f7 0%, #e11d48 60%, transparent 70%)" }} />
      <header className="relative z-10 flex items-center justify-between px-6 md:px-12 py-5">
        <button onClick={() => navigate(user ? "/app" : "/")} data-testid="pricing-back-btn" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex items-center gap-2">
          <img src={BRAND.logo} alt="logo" className="w-8 h-8 rounded-full" />
          <span className="font-heading font-extrabold">Random Cam <span className="brand-gradient-text">Chat</span></span>
        </div>
      </header>

      <main className="relative z-10 px-6 md:px-12 pb-24 max-w-5xl mx-auto">
        <div className="text-center pt-8 pb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full brand-gradient-bg text-white text-sm font-semibold mb-5">
            <Crown className="w-4 h-4" /> Premium
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold">Unlock gender filters</h1>
          <p className="mt-4 text-muted-foreground max-w-xl mx-auto">Free members chat with random strangers. Go Premium to pick your gender and choose exactly who you meet.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {PLANS.map((p) => (
            <div key={p.key} data-testid={`plan-${p.key}`}
              className={`relative rounded-2xl border p-6 flex flex-col ${p.highlight ? "border-primary bg-primary/5" : "border-border bg-card"}`}>
              {p.highlight && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 brand-gradient-bg text-white border-0">Most popular</Badge>
              )}
              <h3 className="font-heading font-bold text-xl">{p.name}</h3>
              <div className="mt-3 flex items-end gap-1">
                <span className="font-heading text-4xl font-extrabold">{p.price}</span>
                <span className="text-muted-foreground mb-1 text-sm">{p.per}</span>
              </div>
              {p.note && <span className="text-xs text-primary mt-1">{p.note}</span>}
              <ul className="mt-6 space-y-2.5 flex-1">
                {PERKS.map((perk) => (
                  <li key={perk} className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" /> {perk}
                  </li>
                ))}
              </ul>
              <Button onClick={() => checkout(p.key)} disabled={loadingKey === p.key} data-testid={`subscribe-${p.key}`}
                className={`mt-6 rounded-full h-11 font-semibold ${p.highlight ? "brand-gradient-bg text-white border-0" : "bg-secondary hover:bg-accent border border-border"}`}>
                {loadingKey === p.key ? <Loader2 className="w-4 h-4 animate-spin" /> : "Subscribe"}
              </Button>
            </div>
          ))}
        </div>
        <p className="text-center text-xs text-muted-foreground mt-8">Secure checkout by Stripe · Cancel anytime · Test card 4242 4242 4242 4242</p>
      </main>
    </div>
  );
}
